import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';
import { Bars3Icon, XMarkIcon, WalletIcon } from '@heroicons/react/24/outline';

function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { currentUser, logout, loading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Add scroll detection for navbar appearance change
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  // Define navigation items based on user role and current page
  const getNavigationItems = () => {
    const baseNavigation = [
      { name: 'Home', href: '/' },
      { name: 'About', href: '/about' },
      { name: 'Investment Plans', href: '/investment-plans' },
      { name: 'Portfolio', href: '/portfolio' },
      { name: 'Contact', href: '/contact' }
    ];

    // Add dashboard link if user is logged in and not an admin
    if (currentUser && !isAdmin && location.pathname !== '/dashboard') {
      baseNavigation.push({
        name: 'Dashboard',
        href: '/dashboard',
        icon: WalletIcon,
        className:
          'text-slate-950 bg-cyan-400 hover:bg-cyan-300 rounded-lg px-4 py-2 shadow-sm ring-1 ring-cyan-300/40 transition-colors'
      });
    }

    return baseNavigation;
  };

  const navigation = getNavigationItems();

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      setIsOpen(false); // Close mobile menu
      await logout();
      // Use replace to prevent going back to protected routes
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error logging out:', error);
      // Continue with navigation even if logout fails
      navigate('/login', { replace: true });
    }
  };

  const menuVariants = {
    closed: {
      opacity: 0,
      scale: 0.95,
      y: -20,
      transition: {
        duration: 0.2,
        ease: 'easeInOut'
      }
    },
    open: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: 'easeOut',
        staggerChildren: 0.05,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    closed: { 
      opacity: 0, 
      y: -10,
      transition: { duration: 0.2 }
    },
    open: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3 }
    }
  };

  // Don't render navigation items while auth is loading
  if (loading) {
    return (
      <>
        <div className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-stone-950/80 backdrop-blur-sm">
          <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
            <Link to="/" className="flex-shrink-0">
              <Logo darkMode />
            </Link>
          </div>
        </div>
        <div className="h-16" />
      </>
    );
  }

  return (
    <>
      <div className="fixed inset-x-0 top-0 z-50">
        <motion.nav
          className={`border-b transition-colors ${
            scrolled
              ? 'border-white/10 bg-stone-950/85 backdrop-blur-sm shadow-[0_10px_30px_rgba(0,0,0,0.35)]'
              : 'border-transparent bg-stone-950/70 backdrop-blur-sm'
          }`}
          initial={{ y: -12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
        >
          <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
            <Link to="/" className="flex-shrink-0 z-10">
              <Logo darkMode />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex md:items-center md:space-x-1 lg:space-x-2">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <motion.div
                    key={item.href}
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Link
                      to={item.href}
                      className={`relative px-3 py-2 text-sm font-medium transition-colors rounded-lg
                        ${item.className || (isActive 
                          ? 'text-cyan-200 bg-white/5 ring-1 ring-cyan-400/25' 
                          : 'text-stone-200 hover:text-white hover:bg-white/5')}`}
                    >
                      {item.icon && <item.icon className="w-4 h-4 mr-2 inline-block" />}
                      {item.name}
                    </Link>
                  </motion.div>
                );
              })}
              
              <div className="hidden md:flex ml-3 items-center space-x-2 pl-3 border-l border-white/10">
                {currentUser ? (
                  <>
                    {isAdmin && location.pathname === '/admin' ? (
                      <motion.button
                        onClick={handleLogout}
                        className="inline-flex items-center justify-center rounded-lg bg-red-500/15 px-4 py-2 text-sm font-semibold text-red-200 ring-1 ring-red-400/25 hover:bg-red-500/20 transition-colors"
                        whileHover={{ y: -1 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Log Out
                      </motion.button>
                    ) : isAdmin ? (
                      <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }}>
                        <Link
                          to="/admin"
                          className="inline-flex items-center justify-center rounded-lg bg-cyan-400 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-cyan-300 transition-colors"
                        >
                          Admin Dashboard
                        </Link>
                      </motion.div>
                    ) : (
                      <motion.button
                        onClick={handleLogout}
                        className="inline-flex items-center justify-center rounded-lg bg-red-500/15 px-4 py-2 text-sm font-semibold text-red-200 ring-1 ring-red-400/25 hover:bg-red-500/20 transition-colors"
                        whileHover={{ y: -1 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Log Out
                      </motion.button>
                    )}
                  </>
                ) : (
                  <>
                    <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }}>
                      <Link
                        to="/login"
                        className="inline-flex items-center justify-center rounded-lg bg-white/5 px-4 py-2 text-sm font-semibold text-stone-100 ring-1 ring-white/10 hover:bg-white/10 transition-colors"
                      >
                        Login
                      </Link>
                    </motion.div>
                    <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }}>
                      <Link
                        to="/signup"
                        className="inline-flex items-center justify-center rounded-lg bg-cyan-400 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-cyan-300 transition-colors"
                      >
                        Create Account
                      </Link>
                    </motion.div>
                  </>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center space-x-3">
              <motion.button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center rounded-lg p-2 text-stone-200 ring-1 ring-white/10 hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/40 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <span className="sr-only">Open main menu</span>
                <AnimatePresence mode="wait">
                  {!isOpen ? (
                    <motion.div
                      key="hamburger"
                      initial={{ rotate: 0 }}
                      animate={{ rotate: 0 }}
                      exit={{ rotate: 90 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Bars3Icon className="block h-6 w-6" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="close"
                      initial={{ rotate: -90 }}
                      animate={{ rotate: 0 }}
                      exit={{ rotate: 90 }}
                      transition={{ duration: 0.2 }}
                    >
                      <XMarkIcon className="block h-6 w-6" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            </div>
          </div>
        </motion.nav>
      </div>
      <div className="h-16" />

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-x-0 top-16 z-40 px-4 md:hidden">
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={menuVariants}
              className="mx-auto max-w-md overflow-hidden rounded-2xl border border-white/10 bg-stone-950/95 shadow-[0_18px_60px_rgba(0,0,0,0.55)] backdrop-blur-sm"
            >
              <div className="relative px-4 py-4 space-y-1">
                {navigation.map((item, index) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <motion.div
                      key={item.href}
                      variants={itemVariants}
                      custom={index}
                    >
                      <Link
                        to={item.href}
                        className={`block rounded-xl px-4 py-3 text-base font-medium transition-colors
                          ${isActive 
                            ? 'text-cyan-200 bg-white/5 ring-1 ring-cyan-400/25' 
                            : 'text-stone-100 hover:bg-white/5'}`}
                        onClick={() => setIsOpen(false)}
                      >
                        {item.name}
                      </Link>
                    </motion.div>
                  );
                })}
                
                <div className="mt-4 space-y-2 border-t border-white/10 pt-4">
                  {currentUser ? (
                    <>
                      {isAdmin && location.pathname === '/admin' ? (
                        <motion.button
                          onClick={handleLogout}
                          className="block w-full rounded-xl bg-red-500/15 px-4 py-3 text-center text-sm font-semibold text-red-200 ring-1 ring-red-400/25 hover:bg-red-500/20 transition-colors"
                          variants={itemVariants}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                        >
                          Log Out
                        </motion.button>
                      ) : isAdmin ? (
                        <motion.div 
                          variants={itemVariants}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                        >
                          <Link
                            to="/admin"
                            className="block w-full rounded-xl bg-cyan-400 px-4 py-3 text-center text-sm font-semibold text-slate-950 hover:bg-cyan-300 transition-colors"
                            onClick={() => setIsOpen(false)}
                          >
                            Admin Dashboard
                          </Link>
                        </motion.div>
                      ) : (
                        <motion.button
                          onClick={handleLogout}
                          className="block w-full rounded-xl bg-red-500/15 px-4 py-3 text-center text-sm font-semibold text-red-200 ring-1 ring-red-400/25 hover:bg-red-500/20 transition-colors"
                          variants={itemVariants}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                        >
                          Log Out
                        </motion.button>
                      )}
                    </>
                  ) : (
                    <>
                      <motion.div 
                        variants={itemVariants}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <Link
                          to="/login"
                          className="block w-full rounded-xl bg-white/5 px-4 py-3 text-center text-sm font-semibold text-stone-100 ring-1 ring-white/10 hover:bg-white/10 transition-colors"
                          onClick={() => setIsOpen(false)}
                        >
                          Login
                        </Link>
                      </motion.div>
                      <motion.div 
                        variants={itemVariants}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <Link
                          to="/signup"
                          className="block w-full rounded-xl bg-cyan-400 px-4 py-3 text-center text-sm font-semibold text-slate-950 hover:bg-cyan-300 transition-colors"
                          onClick={() => setIsOpen(false)}
                        >
                          Create Account
                        </Link>
                      </motion.div>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Navigation;